import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Shield, Lock, Smartphone, Key, AlertTriangle,
  CheckCircle, Eye, EyeOff, RefreshCw, Lightbulb, HelpCircle
} from 'lucide-react';

const AccountSecurityGuide = () => {
  const navigate = useNavigate();

  const securityFeatures = [
    {
      id: 'password',
      title: 'Password',
      icon: Lock,
      color: 'bg-blue-100 text-blue-600',
      description: 'Your first line of defense - use a strong, unique password'
    },
    {
      id: 'mfa',
      title: 'Two-Factor Authentication (2FA)',
      icon: Smartphone,
      color: 'bg-green-100 text-green-600',
      description: 'Add an extra layer of security with your phone'
    },
    {
      id: 'recovery',
      title: 'Recovery Codes',
      icon: Key,
      color: 'bg-purple-100 text-purple-600',
      description: 'Backup codes to access your account if you lose your phone'
    }
  ];

  const faqs = [
    {
      question: "What if I forget my password?",
      answer: "Click 'Forgot Password' on the login page. We'll send a reset link to your email. The link expires in 1 hour for security."
    },
    {
      question: "What authenticator apps work with SuddyFlow?",
      answer: "Any TOTP-based authenticator app works: Google Authenticator, Microsoft Authenticator, Authy, 1Password, or Bitwarden. They all generate the same type of 6-digit codes."
    },
    {
      question: "What if I lose my phone with the authenticator app?",
      answer: "Use one of your recovery codes to log in, then disable 2FA and set it up again with your new phone. This is why saving recovery codes is critical!"
    },
    {
      question: "Can I use the same authenticator for multiple accounts?",
      answer: "Yes! Your authenticator app can hold codes for many accounts - SuddyFlow, Gmail, your bank, etc. Each account gets its own entry in the app."
    },
    {
      question: "Is 2FA required?",
      answer: "Not required, but highly recommended. If someone gets your password (through a data breach, phishing, or guessing), 2FA stops them from accessing your account."
    },
    {
      question: "How often do I need to enter the 2FA code?",
      answer: "Each time you log in from a new device or browser. If you stay logged in, you won't need to re-enter it until your session expires."
    }
  ];

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/help')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Help Center</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Account Security</h1>
          <p className="text-lg text-gray-600">
            Keep your account safe with strong passwords and two-factor authentication.
          </p>
        </div>

        {/* Quick Access */}
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg p-4 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Shield className="text-primary-600" size={24} />
            <span className="font-medium text-gray-900">Ready to secure your account?</span>
          </div>
          <button
            onClick={() => navigate('/settings')}
            className="btn btn-primary text-sm"
          >
            Go to Security Settings
          </button>
        </div>

        {/* Security Features Overview */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Security Features</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {securityFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.id} className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-3 ${feature.color}`}>
                    <Icon size={24} />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Change Password Section */}
        <div className="mb-8 bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center">
                <Lock size={22} />
              </div>
              Changing Your Password
            </h2>
          </div>
          <div className="p-6">
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">How to change your password:</h3>
            <ol className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                <span className="text-gray-700">Go to <strong>Settings → Security</strong> tab</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                <span className="text-gray-700">Enter your <strong>current password</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                <span className="text-gray-700">Enter your <strong>new password</strong> (must be at least 8 characters)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                <span className="text-gray-700"><strong>Confirm</strong> your new password</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">5</span>
                <span className="text-gray-700">Click <strong>Change Password</strong></span>
              </li>
            </ol>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <Lightbulb size={18} />
                Strong Password Tips
              </h4>
              <ul className="space-y-1 text-sm text-blue-800">
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
                  <span>Use at least 12 characters (longer is better)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
                  <span>Mix uppercase, lowercase, numbers, and symbols</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
                  <span>Don't use personal info (birthdays, names, addresses)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
                  <span>Use a unique password - don't reuse from other sites</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
                  <span>Consider using a password manager (1Password, Bitwarden, etc.)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* 2FA Section */}
        <div className="mb-8 bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-10 h-10 bg-green-600 text-white rounded-lg flex items-center justify-center">
                <Smartphone size={22} />
              </div>
              Two-Factor Authentication (2FA)
            </h2>
          </div>
          <div className="p-6">
            <p className="text-gray-700 mb-4">
              Two-factor authentication adds an extra layer of security. Even if someone gets your password, 
              they can't access your account without the code from your phone.
            </p>

            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">How to enable 2FA:</h3>
            <ol className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                <span className="text-gray-700">Go to <strong>Settings → Security</strong> tab</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                <span className="text-gray-700">Click <strong>Enable Two-Factor Authentication</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                <span className="text-gray-700">Open your <strong>authenticator app</strong> (Google Authenticator, Authy, etc.)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                <span className="text-gray-700"><strong>Scan the QR code</strong> shown on screen</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">5</span>
                <span className="text-gray-700">Enter the <strong>6-digit code</strong> from your app to verify</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">6</span>
                <span className="text-gray-700"><strong>Save your recovery codes</strong> in a safe place!</span>
              </li>
            </ol>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                <AlertTriangle size={18} />
                Important: Save Your Recovery Codes!
              </h4>
              <p className="text-sm text-amber-800">
                After enabling 2FA, you'll receive <strong>10 recovery codes</strong>. These are your backup 
                if you lose access to your authenticator app. Store them somewhere safe - like a password manager 
                or printed in a secure location. Each code can only be used once.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <Smartphone size={18} className="text-gray-600" />
                  Recommended Apps
                </h4>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li>• Google Authenticator (free)</li>
                  <li>• Microsoft Authenticator (free)</li>
                  <li>• Authy (free, with cloud backup)</li>
                  <li>• 1Password (paid, includes password manager)</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <RefreshCw size={18} className="text-gray-600" />
                  Managing 2FA
                </h4>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li>• Disable anytime in Security settings</li>
                  <li>• Regenerate recovery codes if needed</li>
                  <li>• Re-enable with a new phone</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Recovery Codes Section */}
        <div className="mb-8 bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-600 text-white rounded-lg flex items-center justify-center">
                <Key size={22} />
              </div>
              Recovery Codes
            </h2>
          </div>
          <div className="p-6">
            <p className="text-gray-700 mb-4">
              Recovery codes are your backup plan if you lose access to your authenticator app. 
              When you enable 2FA, you'll receive 10 one-time-use codes.
            </p>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-purple-900 mb-2">Using a Recovery Code:</h4>
              <ol className="space-y-1 text-sm text-purple-800">
                <li>1. On the 2FA verification screen, click "Use recovery code"</li>
                <li>2. Enter one of your saved recovery codes</li>
                <li>3. You'll be logged in (that code is now used up)</li>
                <li>4. Go to Settings → Security to disable/re-enable 2FA with your new phone</li>
              </ol>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">Where to Store Recovery Codes:</h4>
              <ul className="space-y-1 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} className="text-green-500 mt-0.5" />
                  <span>Password manager (most secure)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} className="text-green-500 mt-0.5" />
                  <span>Printed paper in a safe or lockbox</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={14} className="text-green-500 mt-0.5" />
                  <span>Encrypted file on your computer</span>
                </li>
                <li className="flex items-start gap-2 text-red-600">
                  <AlertTriangle size={14} className="mt-0.5" />
                  <span>NOT in plain text, email, or cloud notes</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* FAQs */}
        <div className="mb-10 bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className={index !== faqs.length - 1 ? 'pb-4 border-b border-gray-100' : ''}>
                <h3 className="font-medium text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-sm text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-8 text-center border-2 border-green-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Secure Your Account Now
          </h2>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            Enable two-factor authentication to protect your business data from unauthorized access.
          </p>
          <button
            onClick={() => navigate('/settings')}
            className="btn btn-primary"
          >
            Go to Security Settings
          </button>
        </div>

        {/* Back to Help Center */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/help')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Help Center
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountSecurityGuide;